import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Collapse,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PhoneIcon from "@material-ui/icons/Phone";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useFormik } from "formik";
import * as Yup from "yup";
import { forgotPassword, resetPasswordPhone } from "slices/loginSlice";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  card: {
    width: "100%",
    maxWidth: 420,
    border: "1px solid #e5e5e5",
    padding: theme.spacing(5, 6),
    position: "relative",
    overflow: "hidden",
    marginTop: "4rem",
    marginBottom: "4rem",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "bold",
    letterSpacing: "-0.3px",
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    color: "#5A5A5A",
    marginBottom: theme.spacing(4),
  },
  label: {
    fontFamily: "Satoshi-medium",
    fontSize: "1rem",
    marginBottom: theme.spacing(0.5),
    display: "block",
  },
  field: {
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      transition: "all 0.2s ease",
      "& fieldset": {
        transition: "border-color 0.2s ease",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 10px",
    },
    "& .MuiFormHelperText-root": {
      fontSize: 12,
      marginLeft: 4,
      color: "#f87171",
    },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
      fontSize: 18,
      color: "#5a5a5a",
    },
  },
  otpRow: {
    display: "flex",
    gap: theme.spacing(1.2),
    marginBottom: theme.spacing(2.5),
  },
  otpField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      fontSize: 20,
      fontWeight: 600,
    },
    "& .MuiOutlinedInput-input": {
      padding: "13px 6px",
      textAlign: "center",
      letterSpacing: 2,
    },
    "& .MuiFormHelperText-root": {
      fontSize: 11,
      color: "#f87171",
    },
  },
  timerBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(1.75),
    borderRadius: 4,
    border: "1px solid #5a5a5a4f",
  },
  timerText: {
    fontFamily: "Satoshi-Medium",
    fontSize: 14,
    color: ({ timeLeft }) =>
      timeLeft > 60 ? "#149b69" : timeLeft > 0 ? "#fbbf24" : "#f87171",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  timerDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "currentColor",
    animation: "$pulse 1.2s ease-in-out infinite",
    marginRight: 8
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.3 },
  },
  resendBtn: {
    fontSize: 12,
    textTransform: "none",
    padding: "4px 10px",
    borderRadius: 4,
    border: "1px solid rgba(167,139,250,0.35)",
    background: "rgba(167,139,250,0.08)",
    "&:hover": { background: "rgba(167,139,250,0.16)" },
  },
  primaryBtn: {
    fontSize: 14,
    letterSpacing: "0.3px",
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.primary.main}`,
    "&:hover": {
      background: "white",
      color: theme.palette.primary.main,
    },
  },
  toast: {
    fontFamily: "Satoshi-Medium",
    display: "flex",
    alignItems: "flex-start",
    gap: '8px',
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    marginBottom: theme.spacing(2.5),
    fontSize: 13,
    lineHeight: 1.5,
  },
  toastSuccess: {
    background: "rgba(52,211,153,0.1)",
    border: "1px solid rgba(52,211,153,0.25)",
    color: "#34d399",
  },
  toastError: {
    background: "rgba(248,113,113,0.1)",
    border: "1px solid rgba(248,113,113,0.25)",
    color: "#f87171",
  },
  divider: {
    borderBottom: "1px solid #5A5A5A",
    margin: theme.spacing(3, 0),
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#5A5A5A",
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  clickToLogin: {
    textAlign: "center",
    textDecoration: "underline",
  }
}));

function useCountdown(initialSeconds, active) {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    setTimeLeft(initialSeconds);
    intervalRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [active, initialSeconds]);

  const reset = () => {
    clearInterval(intervalRef.current);
    setTimeLeft(initialSeconds);
    intervalRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(intervalRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const fmt = `${String(Math.floor(timeLeft / 60)).padStart(2, "0")}:${String(timeLeft % 60).padStart(2, "0")}`;
  return { timeLeft, fmt, reset };
}

export default function ForgotPasswordPhoneNumber() {
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const { timeLeft, fmt, reset: resetTimer } = useCountdown(300, timerActive);

  const classes = useStyles({ timeLeft });
  const dispatch = useDispatch();

  const showToast = (type, msg) => {
    setToast({ type, msg });
    if (type === "success") return; // keep success persistent
    setTimeout(() => setToast(null), 5000);
  };

  const phoneFormik = useFormik({
    initialValues: { phoneNumber: "" },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid phone number (10 digits)")
        .required("Phone number is required"),
    }),
    onSubmit: async (values) => {
      setSendingOtp(true);
      try {
        let result = await dispatch(forgotPassword(values.phoneNumber));
        if (result.status === "OK") {
          setOtpSent(true);
          setTimerActive(true);
        } else {
          throw result
        }
      } catch (err) {
        // console.log(err)
        const errorObj = typeof err !== "object" ? JSON.parse(err) : err;
        showToast("error", errorObj.message || errorObj.reason || errorObj?.error || "Failed to send OTP. Try again.");
      } finally {
        setSendingOtp(false);
      }
    },
  });

  // ── Phase 2: Reset Password ───────────────────────────────────────────────
  const resetFormik = useFormik({
    initialValues: { otp: "", newPassword: "", confirmPassword: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^[0-9]{4}$/, "OTP must be 4 digits")
        .required("OTP is required"),
      newPassword: Yup.string()
        .min(6, "Password Too Short! Minimum Length - 6")
        .max(15, "Password Too Long! Maximum Length - 15")
        .matches(/^(.*[A-Z].*)/, "Must Contain an Upper Case Alphabet")
        .matches(/^(.*[a-z].*)/, "Must Contain a Lower Case Alphabet")
        .matches(/^(.*[0-9].*)/, "Must Contain a number")
        .matches(/^(.*\W.*)/, "Must Contain a special character")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords do not match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values) => {
      setResetting(true);
      try {
        let res = await dispatch(resetPasswordPhone({
          phone: phoneFormik.values.phoneNumber,
          otp: values.otp,
          password: values.newPassword,
        }));
        if (res.Success) {
          showToast("success", "Password reset successfully!");
          setTimerActive(false);
        }
      } catch (error) {
        const err = typeof err !== "object" ? JSON.parse(err) : err;
        showToast("error", err?.error || err?.message || "Reset failed. Please try again.");
      } finally {
        setResetting(false);
      }
    },
  });

  const handleResendOtp = async () => {
    setSendingOtp(true);
    try {
      let result = await dispatch(forgotPassword(phoneFormik.values.phoneNumber));
      if (result.status === "OK") {
        resetTimer();
        resetFormik.setFieldValue("otp", "");
      } else {
        throw result
      }
    } catch (err) {
      showToast("error", "Failed to resend OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  const isSuccess = toast?.type === "success";

  return (
    <>
      <Box className={classes.root}>
        <Paper elevation={0} className={classes.card}>
          {/* Header */}
          <Typography className={classes.title}>Reset Password</Typography>
          <Typography className={classes.subtitle}>
            {!otpSent
              ? "Enter your registered phone number to receive a one-time password."
              : "Enter the OTP sent to your number, then create a new password."}
          </Typography>

          {/* Toast */}
          {toast && (
            <Box
              className={`${classes.toast} ${
                toast.type === "success" ? classes.toastSuccess : classes.toastError
              }`}
            >
              {toast.type === "success" ? (
                <CheckCircleOutlineIcon style={{ fontSize: 18, marginTop: 1 }} />
              ) : (
                <ErrorOutlineIcon style={{ fontSize: 18, marginTop: 1 }} />
              )}
              <span>{toast.msg}</span>
            </Box>
          )}

          {isSuccess && (
            <Typography className={classes.clickToLogin} color="primary">
              <a href="/">Click here to login</a>
            </Typography>
          )}

          {/* Success state — hide form */}
          {!isSuccess && (
            <>
              {/* ── Phone Number ── */}
              <label className={classes.label}>Phone Number</label>
              <Box className={classes.field}>
                <TextField
                  variant="outlined"
                  fullWidth
                  placeholder="e.g. 9876543210"
                  name="phoneNumber"
                  value={phoneFormik.values.phoneNumber}
                  onChange={phoneFormik.handleChange}
                  onBlur={phoneFormik.handleBlur}
                  disabled={otpSent}
                  error={
                    phoneFormik.touched.phoneNumber &&
                    Boolean(phoneFormik.errors.phoneNumber)
                  }
                  helperText={
                    phoneFormik.touched.phoneNumber &&
                    phoneFormik.errors.phoneNumber
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* ── OTP + Password Fields (slide in after OTP sent) ── */}
              <Collapse in={otpSent} timeout={400}>
                <Box>
                  <Box className={classes.divider} />
                  <Typography className={classes.sectionLabel}>
                    Verification & New Password
                  </Typography>

                  {/* Timer */}
                  <Box className={classes.timerBox}>
                    <Box className={classes.timerText}>
                      <Box
                        className={classes.timerDot}
                        style={{ visibility: timeLeft > 0 ? "visible" : "hidden" }}
                      />
                      {timeLeft > 0 ? `OTP expires in ${fmt}` : "OTP expired"}
                    </Box>
                    {timeLeft === 0 && (
                      <Button
                        className={classes.resendBtn}
                        onClick={handleResendOtp}
                        disabled={sendingOtp}
                        size="small"
                      >
                        {sendingOtp ? (
                          <CircularProgress size={12} style={{ color: "#a78bfa", padding:6 }} />
                        ) : (
                          "Resend OTP"
                        )}
                      </Button>
                    )}
                  </Box>

                  {/* OTP */}
                  <label className={classes.label}>One-Time Password</label>
                  <Box className={classes.field}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Enter OTP"
                      name="otp"
                      value={resetFormik.values.otp}
                      onChange={resetFormik.handleChange}
                      onBlur={resetFormik.handleBlur}
                      inputProps={{ maxLength: 6 }}
                      error={
                        resetFormik.touched.otp && Boolean(resetFormik.errors.otp)
                      }
                      helperText={resetFormik.touched.otp && resetFormik.errors.otp}
                    />
                  </Box>

                  {/* New Password */}
                  <label className={classes.label}>New Password</label>
                  <Box className={classes.field}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="At least 6 characters"
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      value={resetFormik.values.newPassword}
                      onChange={resetFormik.handleChange}
                      onBlur={resetFormik.handleBlur}
                      error={
                        resetFormik.touched.newPassword &&
                        Boolean(resetFormik.errors.newPassword)
                      }
                      helperText={
                        resetFormik.touched.newPassword &&
                        resetFormik.errors.newPassword
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowPassword((v) => !v)}
                              // style={{ color: "rgba(255,255,255,0.3)" }}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon style={{ fontSize: 18 }} />
                              ) : (
                                <VisibilityIcon style={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Confirm Password */}
                  <label className={classes.label}>Confirm Password</label>
                  <Box className={classes.field}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="Re-enter your new password"
                      name="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      value={resetFormik.values.confirmPassword}
                      onChange={resetFormik.handleChange}
                      onBlur={resetFormik.handleBlur}
                      error={
                        resetFormik.touched.confirmPassword &&
                        Boolean(resetFormik.errors.confirmPassword)
                      }
                      helperText={
                        resetFormik.touched.confirmPassword &&
                        resetFormik.errors.confirmPassword
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowConfirm((v) => !v)}
                            >
                              {showConfirm ? (
                                <VisibilityOffIcon style={{ fontSize: 18 }} />
                              ) : (
                                <VisibilityIcon style={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Collapse>

              {/* ── CTA Button ── */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                className={classes.primaryBtn}
                disabled={sendingOtp || resetting}
                onClick={
                  otpSent
                    ? resetFormik.handleSubmit
                    : phoneFormik.handleSubmit
                }
              >
                {sendingOtp || resetting ? (
                  <CircularProgress size={20} style={{ color: "#4A3BFF" }} />
                ) : otpSent ? (
                  "Reset Password"
                ) : (
                  "Send OTP"
                )}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
}
